import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { MatrixTableEffect, MatrixTablePredicate } from "src/clients/flow-api";
import { EffectCell } from "src/matrixTableNode/EffectCell";
import { FloatingCodeInputProvider } from "src/matrixTableNode/FloatingCodeInputProvider";
import { MatrixTableComp } from "src/matrixTableNode/MatrixTableComp";
import {
  MatrixTableError,
  MatrixTableNodeForm,
} from "src/matrixTableNode/MatrixTableNodeEditor";
import { PredicateCell } from "src/matrixTableNode/PredicateCell";
import {
  buildMatrixTableActions,
  MatrixTableActions,
} from "src/matrixTableNode/utils";

const columnHelper = createColumnHelper<MatrixTableRow>();

export type MatrixTableRow = {
  row_predicate: MatrixTablePredicate;
  effects: MatrixTableEffect[];
};

const ColumnResizeHandle = ({
  isResizing,
  onResize,
}: {
  isResizing: boolean;
  onResize: (e: unknown) => void;
}) => (
  <div
    className={twJoin(
      "transform-colors absolute bottom-px right-0 h-8 w-1",
      "cursor-col-resize select-none bg-indigo-500 opacity-0 duration-150 hover:opacity-100",
      isResizing && "opacity-100",
    )}
    onMouseDown={onResize}
    onTouchStart={onResize}
  />
);

const buildColumnSortedKey = (columns: MatrixTablePredicate[]) =>
  columns
    .map((col) => col.id)
    .sort()
    .join(",");

const buildColumns = (
  columnPredicates: MatrixTableNodeForm["column_predicates"],
) => {
  const columns = columnPredicates.map((predicateArg, i) => {
    return columnHelper.accessor((row) => row.effects[i], {
      id: `effect-${predicateArg.id}`,
      size: 150,
      minSize: 150,
      maxSize: 320,
      header: (info) => {
        const predicate = info.table.options.meta!.actions!.getColumnPredicate(
          predicateArg.id,
        ) as unknown as MatrixTablePredicate;

        return (
          <div className="relative flex items-center">
            <PredicateCell
              actions={info.table.options.meta!.actions! as MatrixTableActions}
              colIndex={i + 1} // +1 because of the row predicate column
              predicateOperator={predicate.operator}
              predicateValue={predicate.value}
              readOnly={info.table.options.meta!.readonly!}
              rowId=""
              rowIndex={-1}
            />
            <ColumnResizeHandle
              isResizing={info.column.getIsResizing()}
              onResize={info.header.getResizeHandler()}
            />
          </div>
        );
      },
      cell: (cell) => {
        return (
          <EffectCell
            colIndex={cell.column.getIndex()}
            readOnly={cell.table.options.meta!.readonly!}
            rowIndex={cell.row.index}
            value={cell.getValue()?.value ?? ""}
          />
        );
      },
    });
  });

  const rowPredicateColumn = columnHelper.accessor("row_predicate", {
    id: "row_predicate",
    header: (info) => (
      <div className="relative flex bg-white pl-3 pt-3">
        <div className="relative h-[34px] w-full border border-gray-100"></div>
        <ColumnResizeHandle
          isResizing={info.column.getIsResizing()}
          onResize={info.header.getResizeHandler()}
        />
      </div>
    ),
    size: 150,
    minSize: 150,
    maxSize: 320,
    meta: {
      sticky: "left",
    },
    cell: (cell) => {
      return (
        <PredicateCell
          actions={cell.table.options.meta!.actions! as MatrixTableActions}
          colIndex={0}
          predicateOperator={cell.getValue().operator}
          predicateValue={cell.getValue().value}
          readOnly={cell.table.options.meta!.readonly!}
          rowId={cell.row.original.row_predicate.id}
          rowIndex={cell.row.index}
        />
      );
    },
  });

  return [rowPredicateColumn, ...columns];
};

export const MatrixTable = ({
  errors,
  readOnly,
  tableContainerRef,
}: {
  errors: MatrixTableError[];
  readOnly: boolean;
  tableContainerRef: React.RefObject<HTMLDivElement>;
}) => {
  const { watch, setValue, getValues } = useFormContext<MatrixTableNodeForm>();

  const columnPredicates = watch("column_predicates");
  const rowPredicates = watch("row_predicates");
  const effects = watch("grid_effects");
  const columnSortedKey = buildColumnSortedKey(columnPredicates);

  const columns = useMemo(() => {
    return buildColumns(columnPredicates);
  }, [columnSortedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = useMemo(() => {
    return effects.map((rowEffects, index) => {
      return {
        row_predicate: rowPredicates[index],
        effects: rowEffects,
      };
    });
  }, [rowPredicates, effects]);

  const tableActions: MatrixTableActions = useMemo(
    () => buildMatrixTableActions({ getValues, setValue }),
    [getValues, setValue],
  );

  return (
    <div
      ref={tableContainerRef}
      className="relative flex-1 overflow-hidden focus:outline-none"
      tabIndex={-1}
    >
      <FloatingCodeInputProvider
        enabled={!readOnly}
        tableRef={tableContainerRef}
      >
        <MatrixTableComp
          actions={tableActions}
          columns={columns}
          containerRef={tableContainerRef}
          data={data}
          errors={errors}
          readOnly={readOnly}
        />
      </FloatingCodeInputProvider>
    </div>
  );
};
